import React, { useState } from 'react'
import { useMutation } from '@apollo/client'
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material'

import surveyLogo from '../../assets/imgs/surveyLogo.svg'
import SAVE_LEADS from '../../graphqls/saveLeads.gql'
import useGetDynamicToken from '../../hooks/useGetDynamicToken'
import { ContactInfoStep, HowDidYouHearStep, TripalinkResidentStep, MoveTimeStep, HousingTypeStep, BudgetStep } from './steps'
import * as Types from './types'

type FormData = Types.FormData
type SurveyState = Types.SurveyState

const BackToSchoolSurvey: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { getHeaderWithDynamicToken, signatureKeyLoading } = useGetDynamicToken()

  const [saveLeads, { loading: saveLeadsLoading }] = useMutation(SAVE_LEADS, {
    onCompleted: data => {
      console.log('Save leads success:', data)
      setState((prev: SurveyState) => ({
        ...prev,
        isSubmitted: true,
      }))
    },
    onError: error => {
      console.error('Save leads error:', error)
      // TODO: Show error message to user
    },
  })

  // Combined loading state for submit button
  const isSubmitting = saveLeadsLoading || signatureKeyLoading

  const [state, setState] = useState<SurveyState>({
    currentStep: 0,
    totalSteps: 6,
    isSubmitted: false,
    errors: {},
    formData: {
      name: '',
      email: '',
      phone: '',
      howDidYouHear: '',
      isTripalinkResident: '',
      apartmentInfo: '',
      moveTime: '',
      housingType: '',
      budget: '',
    },
  })

  const clearError = (stepIndex: number) => {
    setState((prev: SurveyState) => ({
      ...prev,
      errors: {
        ...prev.errors,
        [stepIndex]: undefined,
      },
    }))
  }

  const getStepError = (index: number) => {
    const { formData } = state
    switch (index) {
      case 0: // Contact Info
        const nameValid = formData.name.trim().length > 0
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        const emailValid = emailRegex.test(formData.email.trim())
        const phoneRegex = /^(\+?1[-. ]?)?\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/
        const phoneValid = phoneRegex.test(formData.phone.trim())

        if (!nameValid || !emailValid || !phoneValid) {
          return {
            name: !nameValid,
            email: !emailValid,
            phone: !phoneValid,
          }
        }
        return null
      case 1: // How did you hear
        return formData.howDidYouHear ? null : true
      case 2: // Tripalink resident (apartment info is optional)
        if (!formData.isTripalinkResident) return true
        return null
      case 3: // Move Time
        return formData.moveTime ? null : true
      case 4: // Housing Type
        return formData.housingType ? null : true
      case 5: // Budget
        return formData.budget ? null : true
      default:
        return null
    }
  }

  const handleNext = () => {
    const error = getStepError(state.currentStep)
    if (error) {
      setState((prev: SurveyState) => ({
        ...prev,
        errors: { ...prev.errors, [prev.currentStep]: error },
      }))
      return
    }
    if (state.currentStep < state.totalSteps - 1) {
      setState((prev: SurveyState) => ({
        ...prev,
        currentStep: prev.currentStep + 1,
      }))
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleBack = () => {
    if (state.currentStep > 0) {
      setState((prev: SurveyState) => ({
        ...prev,
        currentStep: prev.currentStep - 1,
      }))
    }
  }

  const updateFormData = (updates: Partial<FormData>) => {
    setState((prev: SurveyState) => ({
      ...prev,
      formData: { ...prev.formData, ...updates },
    }))
  }

  const handleContactChange = (updates: { name?: string; email?: string; phone?: string }) => {
    updateFormData(updates)
    const stepErrors = (state.errors[0] as any) || {}
    const newStepErrors = { ...stepErrors }
    if (updates.name) delete newStepErrors.name
    if (updates.email) delete newStepErrors.email
    if (updates.phone) delete newStepErrors.phone

    if (Object.keys(newStepErrors).length === 0) {
      clearError(0)
    } else {
      setState((prev: SurveyState) => ({
        ...prev,
        errors: { ...prev.errors, 0: newStepErrors },
      }))
    }
  }

  const handleMoveTimeChange = (val: string) => {
    const wasComplete = !!state.formData.moveTime
    updateFormData({ moveTime: val })
    if (val) clearError(3)

    if (isMobile && !wasComplete && val) {
      setTimeout(() => {
        if (state.currentStep < state.totalSteps - 1) {
          setState((prev: SurveyState) => ({
            ...prev,
            currentStep: prev.currentStep + 1,
          }))
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
      }, 500)
    }
  }

  const handleHousingTypeChange = (val: string) => {
    const wasComplete = !!state.formData.housingType
    updateFormData({ housingType: val })
    if (val) clearError(4)

    if (isMobile && !wasComplete && val) {
      setTimeout(() => {
        if (state.currentStep < state.totalSteps - 1) {
          setState((prev: SurveyState) => ({
            ...prev,
            currentStep: prev.currentStep + 1,
          }))
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
      }, 500)
    }
  }

  const handleHowDidYouHearChange = (val: string) => {
    const wasComplete = !!state.formData.howDidYouHear
    updateFormData({ howDidYouHear: val })
    if (val) clearError(1)

    if (isMobile && !wasComplete && val) {
      setTimeout(() => {
        if (state.currentStep < state.totalSteps - 1) {
          setState((prev: SurveyState) => ({
            ...prev,
            currentStep: prev.currentStep + 1,
          }))
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
      }, 500)
    }
  }

  const handleTripalinkResidentChange = (val: string) => {
    const wasComplete = !!state.formData.isTripalinkResident
    updateFormData({ isTripalinkResident: val })
    if (val) clearError(2)
    // Clear apartment info if user selects "No"
    if (val === 'No') {
      updateFormData({ apartmentInfo: '' })
    }

    // Auto-advance on mobile when selecting "No" or when completing the step
    if (isMobile && !wasComplete && val) {
      // For "No", advance immediately since no apartment info needed
      // For "Yes", let user fill apartment info first (no auto-advance)
      if (val === 'No') {
        setTimeout(() => {
          if (state.currentStep < state.totalSteps - 1) {
            setState((prev: SurveyState) => ({
              ...prev,
              currentStep: prev.currentStep + 1,
            }))
            window.scrollTo({ top: 0, behavior: 'smooth' })
          }
        }, 500)
      }
    }
  }

  const handleApartmentInfoChange = (val: string) => {
    updateFormData({ apartmentInfo: val })
    if (val) clearError(2)
  }

  const handleBudgetChange = (val: string) => {
    updateFormData({ budget: val })
    if (val) clearError(5)
  }

  const parseBudgetRange = (budgetString: string): { min: number; max: number } => {
    switch (budgetString) {
      case 'Under $1,000':
        return { min: 0, max: 1000 }
      case '$1,000-$1,500':
        return { min: 1000, max: 1500 }
      case '$1,500-$2,000':
        return { min: 1500, max: 2000 }
      case '$2000+':
        return { min: 2000, max: 5000 }
      default:
        return { min: 0, max: 5000 }
    }
  }

  const handleSubmit = async () => {
    let hasError = false
    const newErrors: Record<number, any> = {}

    for (let i = 0; i < state.totalSteps; i++) {
      const error = getStepError(i)
      if (error) {
        newErrors[i] = error
        hasError = true
      }
    }

    if (hasError) {
      setState((prev: SurveyState) => ({
        ...prev,
        errors: newErrors,
      }))
      const firstErrorIndex = Object.keys(newErrors)
        .map(Number)
        .sort((a, b) => a - b)[0]

      if (isMobile) {
        setState((prev: SurveyState) => ({
          ...prev,
          currentStep: firstErrorIndex,
        }))
      }
      return
    }

    // Process name field into firstName, middleName, and lastName
    const nameParts = state.formData.name
      .trim()
      .split(' ')
      .filter(part => part.length > 0)
    const firstName = nameParts[0] || ''
    const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : ''
    const middleName = nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : ''

    // Create comment field from survey data (excluding contact info and name)
    const surveyNote = [
      `Source From: 20260129_rooftop_party`,
      `How did you hear: ${state.formData.howDidYouHear}`,
      `Tripalink resident: ${state.formData.isTripalinkResident}`,
      state.formData.apartmentInfo ? `Apartment: ${state.formData.apartmentInfo}` : '',
      `Move Time: ${state.formData.moveTime}`,
      `Housing Type: ${state.formData.housingType}`,
      `Budget: ${state.formData.budget}`,
    ]
      .filter(Boolean)
      .join(' | ')

    // Parse budget range from user selection
    const budgetRange = parseBudgetRange(state.formData.budget)

    // Generate required authentication parameters using dynamic token
    try {
      const { key, timestamp, signature } = await getHeaderWithDynamicToken()

      // Call saveLeads GraphQL mutation
      saveLeads({
        variables: {
          firstName,
          middleName: middleName || undefined,
          lastName,
          email: state.formData.email,
          phone: state.formData.phone,
          budgetMin: budgetRange.min,
          budgetMax: budgetRange.max,
          comment: surveyNote,
          sourceFrom: 'tripalink_website_leave_message',
          originalChannel: '20260129_rooftop_party',
          region: 'USC',
          key,
          timestamp,
          signature,
        },
      })
    } catch (error) {
      console.error('Failed to generate dynamic token:', error)
      // TODO: Show error message to user
    }
  }

  const steps = [
    <ContactInfoStep
      key="step1"
      name={state.formData.name}
      email={state.formData.email}
      phone={state.formData.phone}
      onChange={handleContactChange}
      errors={state.errors[0]}
    />,
    <HowDidYouHearStep key="step2" value={state.formData.howDidYouHear} onChange={handleHowDidYouHearChange} error={!!state.errors[1]} />,
    <TripalinkResidentStep
      key="step3"
      value={state.formData.isTripalinkResident}
      onChange={handleTripalinkResidentChange}
      error={!!state.errors[2]}
      apartmentInfo={state.formData.apartmentInfo}
      onApartmentInfoChange={handleApartmentInfoChange}
      apartmentInfoError={false}
    />,
    <MoveTimeStep key="step4" value={state.formData.moveTime} onChange={handleMoveTimeChange} error={!!state.errors[3]} />,
    <HousingTypeStep key="step5" value={state.formData.housingType} onChange={handleHousingTypeChange} error={!!state.errors[4]} />,
    <BudgetStep key="step6" value={state.formData.budget} onChange={handleBudgetChange} error={!!state.errors[5]} />,
  ]

  return (
    <div className="min-h-screen bg-[#f7f9fc] pb-20">
      {/* Header */}
      <div className="bg-[#111212] px-6 py-6 text-white shadow-md">
        <div className="mx-auto flex max-w-2xl flex-col items-start gap-4 md:flex-row md:items-center md:gap-3">
          <div className="flex items-center gap-4">
            <img src={surveyLogo} alt="Logo" className="h-6" />
            <div className="hidden h-6 w-[1px] bg-white/20 md:block" />
          </div>
          <h1 className="text-lg font-semibold tracking-wide">1/29 Tripalink Rooftop Party!</h1>
        </div>
      </div>

      <div className="mx-auto max-w-[670px] px-4 pt-10">
        {/* Intro Box */}
        {/* {!state.isSubmitted && (
          <div className="mb-10 flex items-start gap-4 rounded-lg bg-[#f7f9fc] p-0 md:bg-transparent">
            <div className="flex gap-3 items-start p-4 bg-white rounded-lg shadow-sm border border-[#e0e2ef] w-full">
              <div className="shrink-0 p-0.5 rounded text-black">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-4 h-4"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4" />
                  <path d="M12 8h.01" />
                </svg>
              </div>
              <p className="text-sm text-[#050a22] leading-5">
                Please answer the following questions so we can find the right home for your needs.
              </p>
            </div>
          </div>
        )} */}

        {state.isSubmitted ? (
          <Box className="flex flex-col items-center justify-center pt-20 px-4">
            <Box className="bg-white p-8 rounded-2xl shadow-lg max-w-md w-full text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-circle-check w-8 h-8 text-green-600"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
              </div>
              <Typography variant="h5" className="text-2xl font-semibold text-[#050a22] mb-2">
                All set!
              </Typography>
              <Typography variant="body1" className="text-gray-600">
                Thank you for checking in!
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box>
            {isMobile ? (
              // Mobile Wizard Layout
              <Box className="relative">
                {/* Progress Bar */}
                <Box className="mb-6 h-1 w-full rounded-full bg-gray-200">
                  <Box
                    className="h-full rounded-full bg-black transition-all duration-300"
                    sx={{
                      width: `${((state.currentStep + 1) / state.totalSteps) * 100}%`,
                    }}
                  />
                </Box>
                <Typography variant="body2" className="mb-4 text-gray-500">
                  Question {state.currentStep + 1} of {state.totalSteps}
                </Typography>

                <Box className="rounded-2xl bg-white p-6 shadow-lg border">{steps[state.currentStep]}</Box>

                {/* Navigation Buttons for Mobile */}
                <div className="mt-8 flex gap-4">
                  {state.currentStep > 0 && (
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={handleBack}
                      className="flex-1"
                      sx={{
                        borderRadius: '12px',
                        borderColor: '#e0e2ef',
                        py: 1.5,
                        fontSize: '16px',
                        textTransform: 'none',
                        color: '#050a22',
                        '&:hover': {
                          borderColor: '#c1c7d0',
                          backgroundColor: 'rgba(5, 10, 34, 0.04)',
                        },
                      }}
                    >
                      Back
                    </Button>
                  )}
                  {state.currentStep < state.totalSteps - 1 ? (
                    <Button
                      type="button"
                      variant="contained"
                      onClick={handleNext}
                      className="flex-1"
                      sx={{
                        borderRadius: '12px',
                        backgroundColor: '#050a22',
                        py: 1.5,
                        fontSize: '16px',
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: 'rgba(5, 10, 34, 0.9)',
                        },
                      }}
                    >
                      Next
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className="flex-1"
                      sx={{
                        borderRadius: '12px',
                        backgroundColor: '#e74f4f',
                        py: 1.5,
                        fontSize: '16px',
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: '#d64444',
                        },
                        '&:disabled': {
                          backgroundColor: '#ccc',
                          color: '#666',
                        },
                      }}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit'}
                    </Button>
                  )}
                </div>
              </Box>
            ) : (
              // Desktop List Layout
              <Box className="bg-white p-12 rounded-2xl shadow-lg border">
                {steps.map((step, index) => (
                  <Box
                    key={index}
                    id={`step-${index}`}
                    className="border-b border-dashed border-gray-200 pb-12 mb-12 last:border-0 last:pb-0 last:mb-0"
                  >
                    {step}
                  </Box>
                ))}

                <div className="pt-4 flex justify-start" id="submit-section">
                  <Button
                    type="button"
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    sx={{
                      minWidth: 140,
                      px: 4,
                      py: 1.5,
                      backgroundColor: '#e74f4f',
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontSize: '16px',
                      fontWeight: 600,
                      boxShadow: '0 10px 15px -3px rgba(231, 79, 79, 0.1), 0 4px 6px -2px rgba(231, 79, 79, 0.05)',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#d64444',
                        boxShadow: '0 20px 25px -5px rgba(231, 79, 79, 0.1), 0 10px 10px -5px rgba(231, 79, 79, 0.04)',
                      },
                      '&:disabled': {
                        backgroundColor: '#ccc',
                        color: '#666',
                        cursor: 'not-allowed',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </Button>
                </div>
              </Box>
            )}
          </Box>
        )}
      </div>
    </div>
  )
}

export default BackToSchoolSurvey
